import { ConversationsChannels, Response } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { Channel } from 'lib-shared-types-channels';

type CommaSeparated<TString extends string> = `${TString}${'' | `,${TString}`}`;
export type IpAddress = `${number}.${number}.${number}.${number}`;

export type BanSortKey = 'banned_at' | 'unbanned_at' | 'expire_at';
export type BanQueryParams = Partial<{
  search: string;
  offset: number;
  limit: number;
  sort_by: string;
  active_state: 'active' | 'inactive' | 'all';
  end_user_id: string;
  banning_agent_id: string;
  unbanning_agent_id: string;
  ip_address: IpAddress;
  channels: CommaSeparated<ConversationsChannels>;
  group_by: string;
}>;
export interface BanQueryOptions {
  params?: BanQueryParams;
  path?: string;
}

/** Selection of common keys between user and agent */
type UserFields = Pick<
  User,
  'id' | 'name' | 'avatar_url' | 'email' | 'phone_number'
>;

export function isIpAddress(ipAddress: string): ipAddress is IpAddress {
  if (!/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/g.test(ipAddress)) {
    return false;
  }
  return ipAddress.split('.').every((byte) => parseInt(byte) < 256);
}

type RequireField<T, TField extends keyof T> = Omit<T, TField> &
  Required<Pick<T, TField>>;

interface Ban {
  /** The id for the ban. */
  readonly id: string;
  /** The id of the organization. */
  readonly organization_id: string;
  /** The time the ban was performed at. */
  readonly banned_at: string;
  /** The agent that performed the ban. */
  readonly banning_agent: Pick<User, keyof UserFields>;
  /** The banned user. */
  readonly end_user?: Pick<User, keyof UserFields>;
  /** Channel that the user is banned on. */
  readonly channel?: Channel;
  /** The id of the conversation from which the ban was performed from. */
  readonly csid?: number;
  /** The reason for banning. */
  readonly banned_reason?: string;
  /** The time the ban expires at. */
  readonly expire_at?: string;
  /** The agent that performed the unban. */
  readonly unbanning_agent?: Pick<User, keyof UserFields>;
  /** The time the unban is performed at. */
  readonly unbanned_at?: string;
  /** The reason for unbanning. */
  readonly unbanned_reason?: string;
  /** The banned ip address of the end user at the time of banning. */
  readonly ip_address?: IpAddress;
  /** Indicates whether the ip address is banned */
  readonly is_ip_address_banned?: boolean;
}

/** Represents the interface for a ban performed on an end user. */
export type UserBan = RequireField<Ban, 'channel' | 'end_user'>;

/** Represents the interface for a ban performed on an ip address. */
export type IpBan = RequireField<Ban, 'ip_address'>;

/** Represents the interface for an active ban performed on an end user. */
export type UserActiveBan = {
  banned_at: string;
  banning_agent_id: string;
  channel: Channel;
  end_user_id: string;
  id: string;
  organization_id: string;
};

export type UsersGroupedByIpBan = RequireField<
  Ban,
  'ip_address' | 'channel' | 'end_user' | 'is_ip_address_banned'
>;

interface BanPeriod {
  /** The length of the ban. */
  units: number;
  /** The unit of the length of the ban. */
  time_unit:
    | 'seconds'
    | 'minutes'
    | 'hours'
    | 'days'
    | 'weeks'
    | 'months'
    | 'years';
}

/** Represents the interface for the ban user API patch endpoint payload. */
export interface BanUserPayload {
  /** Channel the user is banned on. */
  channel: Channel;
  /** The id of the conversation from which the ban was performed from. */
  csid?: number;
  /** The reason for banning. */
  reason?: string;
  /** The length of the ban. */
  ban_period?: BanPeriod;
}

/** Represents the interface for the ban ip address API patch endpoint payload. */
export interface BanIpPayload {
  /** The channel that the ip address should be unbanned from. */
  channel?: Channel;
  /** The id of the conversation from which the ban was performed from. */
  csid?: number;
  /** The reason for banning. */
  reason?: string;
  /** The length of the ban. */
  ban_period?: BanPeriod;
}

/** Represents the interface for the unban user API patch endpoint payload. */
export interface UnbanUserPayload {
  /** The channel that the user should be unbanned from. */
  channel: Channel;
  /** The reason for unbanning the user. */
  reason?: string;
}

/** Represents the interface for the unban ip address API patch endpoint payload. */
export interface UnbanIpPayload {
  /** The channel that the ip address should be unbanned from. */
  channel?: Channel;
  /** The reason for unbanning the ip address. */
  reason?: string;
}

/** Represents the interface for the data in the user bans response. */
export interface UserBansResponseData<TBan> {
  bans: TBan[];
  number_of_bans: number;
}

/** Represents the interface for the data in the ip address bans response. */
export interface IpBansResponseData {
  bans: IpBan[];
  number_of_bans: number;
}

/** Represents the interface for the single user ban API get endpoint response. */
export type UserActiveBansResponse = Response<
  UserBansResponseData<UserActiveBan>
>;

export interface BansByUserResponseDataBans {
  user: User;
  bans: UserBan[];
}

export interface BansByUserResponseData {
  bans: BansByUserResponseDataBans[];
  number_of_users: number;
}

/**
 * Represents the interface for the grouped bans by user API get endpoint
 * response.
 */
export type BansByUserResponse = Response<BansByUserResponseData>;

export interface BansByIpResponseDataBans {
  ip_address: IpAddress;
  bans: UsersGroupedByIpBan[];
}

export interface BansByIpResponseData {
  bans: BansByIpResponseDataBans[];
  number_of_ip_addresses: number;
}

/**
 * Represents the interface for the grouped bans by ip address API get endpoint
 * response.
 */
export type BansByIpResponse = Response<BansByIpResponseData>;

/** Represents the interface for the ban user API patch endpoint response */
export type BanUserResponse = Response<UserBan>;

/** Represents the interface for the ban ip address API patch endpoint response */
export type BanIpResponse = Response<IpBan>;

/** Represents the interface for the unban user API patch endpoint response */
export type UnbanUserResponse = Response<UserBan>;

/** Represents the interface for the unban ip address API patch endpoint response */
export type UnbanIpResponse = Response<IpBan>;
